import React, { useState } from 'react';

const CustomEventModal = ({ isOpen, onClose, onSave }) => {
  const [eventData, setEventData] = useState({
    name: '',
    icon: '🎉',
    startTime: '',
    duration: 60,
    category: 'reception',
    description: '',
    bufferBefore: 0,
    bufferAfter: 0
  });

  const icons = ['💒', '🎉', '📸', '🥂', '💃', '🍽️', '🎵', '💐', '🎂', '💕', '✨', '🌟', '🎊', '🎈', '💍', '👰'];
  const categories = [
    { value: 'preparation', label: 'Preparation', color: 'rose' },
    { value: 'ceremony', label: 'Ceremony', color: 'gold' },
    { value: 'photography', label: 'Photography', color: 'purple' },
    { value: 'reception', label: 'Reception', color: 'emerald' },
    { value: 'entertainment', label: 'Entertainment', color: 'blue' },
    { value: 'other', label: 'Other', color: 'gray' }
  ];

  const handleSave = () => {
    if (!eventData.name || !eventData.startTime) {
      alert('Please fill in event name and start time');
      return;
    }
    onSave(eventData);
    handleClose();
  };

  const handleClose = () => {
    setEventData({
      name: '',
      icon: '🎉',
      startTime: '',
      duration: 60,
      category: 'reception',
      description: '',
      bufferBefore: 0,
      bufferAfter: 0
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-lg z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-gradient-to-br from-white/95 via-blue-50/30 to-cyan-50/30 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-500 p-8 rounded-t-3xl">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-4 left-4 text-4xl animate-pulse">✨</div>
            <div className="absolute top-8 right-8 text-3xl animate-bounce">🎊</div>
            <div className="absolute bottom-4 left-1/3 text-2xl animate-pulse">💫</div>
          </div>
          
          <div className="relative">
            <h2 className="text-3xl font-serif font-bold text-white mb-2">Create Custom Event</h2>
            <p className="text-white/90">Add a special moment to your timeline</p>
          </div>
          
          <button onClick={handleClose} className="absolute top-6 right-6 text-white/80 hover:text-white text-2xl">✕</button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6">
          
          {/* Event Name */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Event Name *</label>
            <input
              type="text"
              value={eventData.name}
              onChange={(e) => setEventData({ ...eventData, name: e.target.value })}
              placeholder="e.g., Grand Entrance, Cake Cutting"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-400 transition-all"
            />
          </div>

          {/* Icon Picker */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Choose Icon</label>
            <div className="grid grid-cols-8 gap-2">
              {icons.map((icon) => (
                <button
                  key={icon}
                  onClick={() => setEventData({ ...eventData, icon })}
                  className={`text-3xl p-3 rounded-xl transition-all hover:scale-110 ${
                    eventData.icon === icon 
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 shadow-lg scale-110' 
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {/* Start Time */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Start Time *</label>
            <input
              type="time"
              value={eventData.startTime}
              onChange={(e) => setEventData({ ...eventData, startTime: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-400 transition-all"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Category</label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setEventData({ ...eventData, category: cat.value })}
                  className={`px-4 py-3 rounded-xl font-medium transition-all ${
                    eventData.category === cat.value
                      ? `bg-${cat.color}-500 text-white shadow-lg`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Description (Optional)</label>
            <textarea
              value={eventData.description}
              onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
              placeholder="Add details about this event..."
              rows="3"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-400 transition-all resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              onClick={handleSave}
              className="flex-1 bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-500 text-white py-3 px-6 rounded-xl font-bold hover:shadow-lg transform hover:scale-105 transition-all"
            >
              ✨ Add Event
            </button>
            <button
              onClick={handleClose}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomEventModal;
